import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Loader2,
  Minimize2,
  Maximize2,
  Sparkles
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [visitorId] = useState(() => {
    let vid = localStorage.getItem("chatbot_visitor_id");
    if (!vid) {
      vid = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("chatbot_visitor_id", vid);
    }
    return vid;
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setUnreadCount(0);
    }
  }, [messages, isOpen]);

  const loadSession = useCallback(async (sid) => {
    try {
      const response = await axios.get(`${API}/chatbot/sessions/${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setSessionId(sid);
        setMessages(response.data.messages);
      }
    } catch (error) {
      localStorage.removeItem("chatbot_widget_session_id");
    }
  }, []);

  // Load existing session
  useEffect(() => {
    const savedSessionId = localStorage.getItem("chatbot_widget_session_id");
    if (savedSessionId) {
      loadSession(savedSessionId);
    }
  }, [loadSession]);

  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    setInputValue("");
    setIsLoading(true);

    const userMessage = {
      message_id: `temp_${Date.now()}`,
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(`${API}/chatbot/message`, {
        message: text,
        session_id: sessionId,
        visitor_id: visitorId
      });

      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
        localStorage.setItem("chatbot_widget_session_id", response.data.session_id);
      }

      const aiMessage = {
        message_id: response.data.message_id,
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString(),
        context_used: response.data.context_used
      };
      setMessages(prev => [...prev, aiMessage]);

      if (!isOpen || isMinimized) {
        setUnreadCount(prev => prev + 1);
      }

    } catch (error) {
      // Error handled silently
      toast.error("Failed to send message");
      setMessages(prev => prev.filter(m => m.message_id !== userMessage.message_id));
      setInputValue(text);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  return (
    <>
      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? "60px" : "500px"
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-40 right-6 w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-[998] flex flex-col"
            style={{ maxHeight: "calc(100vh - 180px)" }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#425B76] to-[#2D4156] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Support Assistant</h3>
                  <p className="text-xs text-white/70">Powered by AI</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                  onClick={() => setIsMinimized(!isMinimized)}
                >
                  {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                  onClick={toggleOpen}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            {!isMinimized && (
              <>
                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                          <MessageCircle className="w-6 h-6 text-violet-600" />
                        </div>
                        <p className="text-sm text-slate-600 font-medium">Hi! How can I help you?</p>
                        <p className="text-xs text-slate-400 mt-1">Ask me anything about TalentDesk</p>
                      </div>
                    ) : (
                      messages.map((message) => (
                        <div
                          key={message.message_id}
                          className={`flex gap-2 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                        >
                          <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                            message.role === "user"
                              ? "bg-[#425B76]"
                              : "bg-gradient-to-br from-violet-500 to-purple-600"
                          }`}>
                            {message.role === "user" ? (
                              <User className="w-3.5 h-3.5 text-white" />
                            ) : (
                              <Bot className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                          <div className={`max-w-[75%] rounded-xl px-3 py-2 ${
                            message.role === "user"
                              ? "bg-[#425B76] text-white"
                              : "bg-slate-100 text-slate-800"
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      ))
                    )}
                    {isLoading && (
                      <div className="flex gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                          <Bot className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="bg-slate-100 rounded-xl px-4 py-3">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-3 border-t border-slate-200 bg-slate-50">
                  <div className="flex gap-2">
                    <Textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder="Type a message..."
                      className="min-h-[40px] max-h-[80px] resize-none text-sm bg-white"
                      disabled={isLoading}
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      size="icon"
                      className="bg-[#425B76] hover:bg-[#2D4156] h-10 w-10 flex-shrink-0"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleOpen}
        className="fixed bottom-24 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-[#425B76] to-[#2D4156] text-white shadow-lg shadow-[#425B76]/30 flex items-center justify-center z-[999] hover:shadow-xl transition-shadow"
        data-testid="chat-widget-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-medium">
                {unreadCount}
              </span>
            )}
          </>
        )}
      </motion.button>
    </>
  );
};

export default ChatWidget;
